/*
 * Copyright (c) 2020, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: MIT
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/MIT
 */

const { getOwnPropertyDescriptor, hasOwnProperty } = Object;

const {
  addEventListener,
  getAttribute,
  getBoundingClientRect,
  getElementsByTagName,
  getElementsByTagNameNS,
  getElementsByClassName,
  matches,
  closest,
  hasAttribute,
  querySelector,
  querySelectorAll,
  removeAttribute,
  removeEventListener,
  setAttribute,
} = Element.prototype;

const attachShadow: (init: ShadowRootInit) => ShadowRoot = hasOwnProperty.call(Element.prototype, 'attachShadow')
  ? Element.prototype.attachShadow
  : () => {
      throw new TypeError(
        'attachShadow() is not supported in current browser. Load the @lwc/synthetic-shadow polyfill and use Lightning Web Components'
      );
    };
const childElementCountGetter: (this: ParentNode) => number = getOwnPropertyDescriptor(
  Element.prototype,
  'childElementCount'
)!.get!;

const firstElementChildGetter: (this: ParentNode) => Element | null = getOwnPropertyDescriptor(
  Element.prototype,
  'firstElementChild'
)!.get!;

const lastElementChildGetter: (this: ParentNode) => Element | null = getOwnPropertyDescriptor(
  Element.prototype,
  'lastElementChild'
)!.get!;

const innerHTMLDescriptor = getOwnPropertyDescriptor(Element.prototype, 'innerHTML');

const innerHTMLGetter: (this: Element) => string = innerHTMLDescriptor!.get!;
const innerHTMLSetter: (this: Element, s: string) => void = innerHTMLDescriptor!.set!;

const outerHTMLDescriptor = getOwnPropertyDescriptor(Element.prototype, 'outerHTML');

const outerHTMLGetter: (this: Element) => string = outerHTMLDescriptor!.get!;
const outerHTMLSetter: (this: Element, s: string) => void = outerHTMLDescriptor!.set!;

const tagNameGetter: (this: Element) => string = getOwnPropertyDescriptor(Element.prototype, 'tagName')!.get!;

const tabIndexDescriptor = getOwnPropertyDescriptor(HTMLElement.prototype, 'tabIndex');
const tabIndexGetter: (this: HTMLElement) => number = tabIndexDescriptor!.get!;
const tabIndexSetter: (this: HTMLElement, v: any) => void = tabIndexDescriptor!.set!;

const childrenGetter: (this: ParentNode) => HTMLCollectionOf<Element> = getOwnPropertyDescriptor(
  Element.prototype,
  'children'
)!.get!;

const shadowRootGetter: (this: Element) => ShadowRoot | null = getOwnPropertyDescriptor(
  Element.prototype,
  'shadowRoot'
)!.get!;

export {
  addEventListener,
  attachShadow,
  childrenGetter,
  childElementCountGetter,
  firstElementChildGetter,
  getAttribute,
  getBoundingClientRect,
  getElementsByClassName,
  getElementsByTagName,
  getElementsByTagNameNS,
  hasAttribute,
  innerHTMLGetter,
  innerHTMLSetter,
  lastElementChildGetter,
  matches,
  closest,
  outerHTMLGetter,
  outerHTMLSetter,
  querySelector,
  querySelectorAll,
  removeAttribute,
  removeEventListener,
  setAttribute,
  shadowRootGetter,
  tagNameGetter,
  tabIndexGetter,
  tabIndexSetter,
};
